import { useMemo, useState } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, Download, FileBarChart } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Button, EmptyState, Skeleton } from '../ui';
import { useQuery } from '../gql/hooks';
import {
  CLIENT_QUOTA_REPORT_QUERY,
  type ClientQuotaReportQueryResult,
  type ClientQuotaReportQueryVariables,
} from '../gql/operations';
import type { ClientQuotaReportRow } from '../gql/types';
import { PageHeader } from '../features/shared/PageHeader';
import { InlineBanner } from '../features/shared/InlineBanner';
import { EnforcementModeBadge } from './QuotaEditorPage';
import { formatNumber } from '../lib/format';

// Monthly quota report (2026-08-09). One row per client for a picked UTC month: request +
// token aggregates vs the client's monthly caps, utilisation bars, and over-cap flags.
// CSV export is generated client-side from the same rows the table renders, so the file
// always matches what the operator is looking at.

/** The current month as "YYYY-MM" (UTC — matches the backend's month bucketing). */
function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

/** Shift a "YYYY-MM" string by +-1 month. */
function shiftMonth(month: string, delta: number): string {
  const [yearRaw, monthRaw] = month.split('-');
  const year = Number(yearRaw);
  const monthIndex = Number(monthRaw) - 1 + delta;
  const date = new Date(Date.UTC(year, monthIndex, 1));
  return date.toISOString().slice(0, 7);
}

/** Human label for a "YYYY-MM" month ("August 2026"). */
function monthLabel(month: string): string {
  const [yearRaw, monthRaw] = month.split('-');
  const date = new Date(Date.UTC(Number(yearRaw), Number(monthRaw) - 1, 1));
  return date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Percent used (0-100+, uncapped) or null when the dimension has no cap. */
function utilisation(used: number, cap: number | null | undefined): number | null {
  if (cap == null || cap <= 0) return null;
  return (used / cap) * 100;
}

const CSV_HEADER = [
  'client_id',
  'client_name',
  'month',
  'requests',
  'prompt_tokens',
  'completion_tokens',
  'reasoning_tokens',
  'total_tokens',
  'monthly_token_cap',
  'monthly_request_cap',
  'enforcement_mode',
  'over_token_cap',
  'over_request_cap',
].join(',');

/** Escape one CSV cell (client names may contain commas/quotes). */
function csvCell(value: string | number | boolean | null | undefined): string {
  if (value == null) return '';
  const raw = String(value);
  return /[",\n]/.test(raw) ? `"${raw.replace(/"/g, '""')}"` : raw;
}

function toCsv(rows: readonly ClientQuotaReportRow[]): string {
  const lines = rows.map((row) =>
    [
      row.clientId,
      row.clientName,
      row.month,
      row.requests,
      row.promptTokens,
      row.completionTokens,
      row.reasoningTokens,
      row.totalTokens,
      row.monthlyTokenCap,
      row.monthlyRequestCap,
      row.enforcementMode?.toLowerCase(),
      row.overTokenCap,
      row.overRequestCap,
    ]
      .map(csvCell)
      .join(','),
  );
  return [CSV_HEADER, ...lines].join('\n');
}

/** Trigger a browser download of the report as CSV. */
function downloadCsv(rows: readonly ClientQuotaReportRow[], month: string): void {
  const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `quota-report-${month}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function UtilisationBar({
  used,
  cap,
  over,
}: {
  used: number;
  cap: number | null | undefined;
  over: boolean;
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const percent = utilisation(used, cap);

  if (percent == null) {
    return (
      <span className={`text-xs ${isDark ? 'text-white/30' : 'text-slate-400'}`}>no cap</span>
    );
  }

  const clamped = Math.min(percent, 100);
  const tone = over
    ? 'bg-rose-500'
    : percent >= 80
      ? 'bg-amber-500'
      : 'bg-emerald-500';

  return (
    <div className="flex items-center gap-2">
      <div
        className={`h-1.5 w-24 overflow-hidden rounded-full ${
          isDark ? 'bg-white/10' : 'bg-slate-200'
        }`}
        role="progressbar"
        aria-valuenow={Math.round(percent)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${clamped}%` }} />
      </div>
      <span
        className={`tabular-nums text-xs font-medium ${
          over
            ? 'text-rose-500'
            : isDark
              ? 'text-white/60'
              : 'text-slate-600'
        }`}
      >
        {Math.round(percent)}%
      </span>
    </div>
  );
}

export function MonthlyQuotaReportPage() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [month, setMonth] = useState(currentMonth);

  const { data, loading, error } = useQuery<
    ClientQuotaReportQueryResult,
    ClientQuotaReportQueryVariables
  >(CLIENT_QUOTA_REPORT_QUERY, { month });

  const rows = useMemo(() => data?.clientQuotaReport ?? [], [data]);
  const overCapRows = rows.filter((row) => row.overTokenCap || row.overRequestCap);
  const isCurrentMonth = month === currentMonth();

  const headCell = `px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-widest ${
    isDark ? 'text-white/40' : 'text-slate-400'
  }`;
  const bodyCell = 'px-4 py-3 align-middle';

  return (
    <div>
      <PageHeader
        title="Monthly quota report"
        description="Per-client usage against monthly budgets — tokens and requests."
        breadcrumbs={[{ label: 'Quotas', to: '/quotas' }, { label: 'Monthly report' }]}
        actions={
          <Button
            variant="secondary"
            onClick={() => downloadCsv(rows, month)}
            disabled={rows.length === 0}
            leftIcon={<Download className="h-4 w-4" aria-hidden="true" />}
          >
            Export CSV
          </Button>
        }
      />

      {/* Month picker: prev / label / next. Next is capped at the current month — future
          months have no usage by definition. */}
      <div className="mb-6 flex items-center gap-3">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setMonth((m) => shiftMonth(m, -1))}
          aria-label="Previous month"
          leftIcon={<ChevronLeft className="h-4 w-4" aria-hidden="true" />}
        >
          Prev
        </Button>
        <span
          data-testid="report-month"
          className={`min-w-40 text-center text-sm font-semibold ${
            isDark ? 'text-white/90' : 'text-slate-900'
          }`}
        >
          {monthLabel(month)}
        </span>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setMonth((m) => shiftMonth(m, 1))}
          disabled={isCurrentMonth}
          aria-label="Next month"
          leftIcon={<ChevronRight className="h-4 w-4" aria-hidden="true" />}
        >
          Next
        </Button>
      </div>

      {overCapRows.length > 0 && (
        <InlineBanner
          tone="warning"
          role="status"
          testId="over-cap-banner"
          icon={<AlertTriangle className="h-4 w-4" aria-hidden="true" />}
        >
          <strong>{overCapRows.length}</strong>{' '}
          {overCapRows.length === 1 ? 'client is' : 'clients are'} over budget this month:{' '}
          {overCapRows.map((row) => row.clientName).join(', ')}.
        </InlineBanner>
      )}

      {error && (
        <InlineBanner tone="error">Couldn’t load the report: {error.message}</InlineBanner>
      )}

      {loading && (
        <div className="space-y-3" data-testid="report-loading">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="block" height={56} />
          ))}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <EmptyState
          icon={<FileBarChart className="h-16 w-16" aria-hidden="true" />}
          title="No clients to report on"
          description="Once clients exist and serve traffic, their monthly usage lands here."
        />
      )}

      {!loading && rows.length > 0 && (
        <div
          className={`overflow-x-auto rounded-2xl border ${
            isDark ? 'border-white/[0.06]' : 'border-slate-200 shadow-sm'
          }`}
        >
          <table className="w-full min-w-[860px] border-collapse text-sm">
            <thead>
              <tr
                className={
                  isDark ? 'border-b border-white/[0.06]' : 'border-b border-slate-200'
                }
              >
                <th scope="col" className={headCell}>
                  Client
                </th>
                <th scope="col" className={headCell}>
                  Requests
                </th>
                <th scope="col" className={headCell}>
                  Request budget
                </th>
                <th scope="col" className={headCell}>
                  Tokens
                </th>
                <th scope="col" className={headCell}>
                  Token budget
                </th>
                <th scope="col" className={headCell}>
                  Mode
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const over = row.overTokenCap || row.overRequestCap;
                return (
                  <tr
                    key={row.clientId}
                    data-testid={`report-row-${row.clientId}`}
                    className={`${
                      isDark ? 'border-b border-white/[0.04]' : 'border-b border-slate-100'
                    } ${over ? (isDark ? 'bg-rose-500/[0.06]' : 'bg-rose-50/60') : ''}`}
                  >
                    <td className={bodyCell}>
                      <span
                        className={`font-medium ${isDark ? 'text-white/90' : 'text-slate-900'}`}
                      >
                        {row.clientName}
                      </span>
                      {over && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-rose-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-rose-500">
                          Over budget
                        </span>
                      )}
                    </td>
                    <td className={`${bodyCell} tabular-nums`}>
                      {formatNumber(row.requests)}
                      {row.monthlyRequestCap != null && (
                        <span className={isDark ? 'text-white/40' : 'text-slate-400'}>
                          {' '}
                          / {formatNumber(row.monthlyRequestCap)}
                        </span>
                      )}
                    </td>
                    <td className={bodyCell}>
                      <UtilisationBar
                        used={row.requests}
                        cap={row.monthlyRequestCap}
                        over={row.overRequestCap}
                      />
                    </td>
                    <td className={`${bodyCell} tabular-nums`}>
                      <span title={`${formatNumber(row.promptTokens)} prompt · ${formatNumber(row.completionTokens)} completion · ${formatNumber(row.reasoningTokens)} reasoning`}>
                        {formatNumber(row.totalTokens)}
                      </span>
                      {row.monthlyTokenCap != null && (
                        <span className={isDark ? 'text-white/40' : 'text-slate-400'}>
                          {' '}
                          / {formatNumber(row.monthlyTokenCap)}
                        </span>
                      )}
                    </td>
                    <td className={bodyCell}>
                      <UtilisationBar
                        used={row.totalTokens}
                        cap={row.monthlyTokenCap}
                        over={row.overTokenCap}
                      />
                    </td>
                    <td className={bodyCell}>
                      {row.enforcementMode ? (
                        <EnforcementModeBadge mode={row.enforcementMode} />
                      ) : (
                        <span
                          className={`text-xs ${isDark ? 'text-white/30' : 'text-slate-400'}`}
                        >
                          no quota
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
